import React, {createContext, useContext, useMemo, useCallback} from 'react';
import {useGetEnabledFeatures} from '../api/admin/useGetEnabledFeatures.ts';
import {useAuthContext} from './AuthContext.tsx';
import isLocalOrDevEnvironment from '../lib/isLocalOrDevEnvironment.ts';

export interface FeatureFlagsState {
    enabledFeatures: string[];
    isEnabled: (feature: string) => boolean;
    isDemoModeEnabled: boolean;
    isDemoMode: boolean;
    isLoading: boolean;
}

const defaultFeatureFlagsState: FeatureFlagsState = {
    enabledFeatures: [],
    isEnabled: () => {
        return false;
    },
    isDemoModeEnabled: false,
    isDemoMode: false,
    isLoading: true
};

export const FeatureFlagsContext = createContext<FeatureFlagsState>(
    defaultFeatureFlagsState
);

export const FeatureFlagsProvider: React.FC<{children: React.ReactNode}> = ({
    children
}) => {
    const {data: rawEnabledFeatures, isPending} = useGetEnabledFeatures();
    const {isThetaAdmin} = useAuthContext();

    const enabledFeatures = useMemo(
        () => rawEnabledFeatures ?? [],
        [rawEnabledFeatures]
    );

    const isEnabled = useCallback(
        (feature: string) => {
            return enabledFeatures.includes(feature);
        },
        [enabledFeatures]
    );

    const isDemoModeEnabled = useMemo(() => {
        const host = window.location.hostname;
        const allowedDemoDomains = [
            'azure-uat.thetaassist.ai',
            'demo4.thetaassist.ai'
        ];
        const isDemoDomain =
            allowedDemoDomains.includes(host) || isLocalOrDevEnvironment;

        return enabledFeatures.includes('demo_mode') && isDemoDomain;
    }, [enabledFeatures]);

    const isDemoMode = useMemo(() => {
        return isDemoModeEnabled && !isThetaAdmin;
    }, [isDemoModeEnabled, isThetaAdmin]);

    const contextValue = useMemo<FeatureFlagsState>(() => {
        return {
            enabledFeatures,
            isEnabled,
            isDemoModeEnabled,
            isDemoMode,
            isLoading: isPending
        };
    }, [enabledFeatures, isEnabled, isDemoModeEnabled, isDemoMode, isPending]);

    return (
        <FeatureFlagsContext.Provider value={contextValue}>
            {children}
        </FeatureFlagsContext.Provider>
    );
};

export function useFeatureFlags(): FeatureFlagsState {
    const featureFlagsContext = useContext(FeatureFlagsContext);
    if (!featureFlagsContext) {
        throw new Error(
            'useFeatureFlags must be used within an FeatureFlagsProvider'
        );
    }
    return featureFlagsContext;
}
